import type { DragEvent } from 'react'
import type { Deal } from '@/constants/crm'

import { useMemo, useState } from 'react'
import Button from '@/components/ui/button'
import CountBadge from '@/components/ui/count-badge'
import FilterMenu from '@/components/generals/filter-menu'
import DealCard from '@/components/deals/deal-card'
import { ViewToolbar } from '@/components/generals/view-parts'
import { OWNERS } from '@/constants/companies'
import { DEAL_STAGES, PIPELINES } from '@/constants/crm'
import { formatCompactMoney, formatMoney } from '@/lib/companies'
import { apiRequest } from '@/lib/client-api'
import { indexById, isOpenDeal, weightedValue } from '@/lib/crm'
import { cn } from '@/lib/utils'
import { useCrmStore } from '@/stores/crm-store-provider'
import PlusIcon from '@/assets/images/_common/plus.svg'

const ALL = 'all'

const OWNER_OPTIONS = [
    { value: ALL, label: "All Owners" },
    ...OWNERS.map(owner => ({ value: owner.name, label: owner.name }))
]

const PIPELINE_OPTIONS = [
    { value: ALL, label: "All Pipelines" },
    ...PIPELINES.map(pipeline => ({ value: pipeline.label, label: pipeline.label }))
]

const DOT_CLASS: Record<string, string> = {
    blue: 'bg-(--tag-blue-text)',
    purple: 'bg-(--tag-purple-text)',
    amber: 'bg-(--tag-amber-text)',
    orange: 'bg-(--tag-orange-text)',
    green: 'bg-success',
    red: 'bg-danger'
}

interface DealsBoardProps {
    pipeline?: string
}

export default function DealsBoard({ pipeline }: DealsBoardProps) {
    const today = useCrmStore(state => state.today)
    const status = useCrmStore(state => state.status)
    const deals = useCrmStore(state => state.deals)
    const companies = useCrmStore(state => state.companies)
    const upsertDeal = useCrmStore(state => state.upsertDeal)
    const openDialog = useCrmStore(state => state.openDialog)
    const openDetail = useCrmStore(state => state.openDetail)
    const showNotice = useCrmStore(state => state.showNotice)
    const [owner, setOwner] = useState(ALL)
    const [pipelineFilter, setPipelineFilter] = useState(ALL)
    const [draggingId, setDraggingId] = useState<string | null>(null)
    const [dropStage, setDropStage] = useState<string | null>(null)

    const activePipeline = pipeline ?? (pipelineFilter === ALL ? null : pipelineFilter)
    const companyIndex = useMemo(() => indexById(companies), [companies])
    const visible = useMemo(
        () => deals.filter(deal => (owner === ALL || deal.owner === owner) && (!activePipeline || deal.pipeline === activePipeline)),
        [deals, owner, activePipeline]
    )
    const open = visible.filter(isOpenDeal)
    const openValue = open.reduce((sum, deal) => sum + deal.value, 0)
    const weighted = open.reduce((sum, deal) => sum + weightedValue(deal), 0)

    async function moveDeal(deal: Deal, stage: string) {
        if (deal.stage === stage) return

        upsertDeal({ ...deal, stage })

        const result = await apiRequest<{ deal: Deal }>(`/api/deals/${deal.id}`, 'PATCH', { stage })

        if (!result.ok) {
            upsertDeal(deal)
            showNotice('error', result.message)

            return
        }

        upsertDeal(result.data.deal)
        showNotice('success', `${deal.name} moved to ${stage}.`)
    }

    function handleDrop(event: DragEvent<HTMLElement>, stage: string) {
        event.preventDefault()

        const id = event.dataTransfer.getData('text/plain') || draggingId
        const deal = deals.find(item => item.id === id)

        setDropStage(null)
        setDraggingId(null)

        if (deal) moveDeal(deal, stage)
    }

    return (
        <>
            <ViewToolbar>
                <div className='flex min-w-0 flex-wrap items-center gap-2'>
                    <FilterMenu label="Owner" value={owner} options={OWNER_OPTIONS} onChange={setOwner} />
                    {!pipeline && (
                        <FilterMenu label="Pipeline" value={pipelineFilter} options={PIPELINE_OPTIONS} onChange={setPipelineFilter} />
                    )}
                    <span className='caption-style text-subtle hidden items-center gap-3 pl-1 md:flex'>
                        <span>
                            <span className='text-foreground tabular-nums'>{open.length}</span> open
                        </span>
                        <span>
                            <span className='text-foreground tabular-nums'>{formatCompactMoney(openValue)}</span> pipeline
                        </span>
                        <span>
                            <span className='text-foreground tabular-nums'>{formatCompactMoney(weighted)}</span> weighted
                        </span>
                    </span>
                </div>
                <Button
                    variant='primary'
                    size='sm'
                    onClick={() => openDialog('deal', activePipeline ? { pipeline: activePipeline } : {})}
                >
                    <PlusIcon aria-hidden className='size-3' />
                    New Deal
                </Button>
            </ViewToolbar>

            <div className='border-border flex min-h-0 flex-1 gap-3 overflow-x-auto border-t p-4'>
                {DEAL_STAGES.map(stage => {
                    const stageDeals = visible.filter(deal => deal.stage === stage.value)
                    const total = stageDeals.reduce((sum, deal) => sum + deal.value, 0)
                    const isDropTarget = dropStage === stage.value && draggingId !== null

                    return (
                        <section
                            key={stage.value}
                            aria-label={`${stage.value} stage`}
                            onDragOver={event => {
                                if (!draggingId) return
                                event.preventDefault()
                                event.dataTransfer.dropEffect = 'move'
                                if (dropStage !== stage.value) setDropStage(stage.value)
                            }}
                            onDragLeave={event => {
                                if (!event.currentTarget.contains(event.relatedTarget as Node | null)) setDropStage(null)
                            }}
                            onDrop={event => handleDrop(event, stage.value)}
                            data-drop={isDropTarget}
                            className='border-border ease-power3-out flex w-[276px] shrink-0 flex-col rounded-xl border bg-white/[0.015] transition-[border-color,background-color] duration-150 data-[drop=true]:border-line-strong data-[drop=true]:bg-white/[0.04]'
                        >
                            <header className='flex shrink-0 flex-col gap-2 px-3 pt-3 pb-2'>
                                <div className='flex items-center justify-between gap-2'>
                                    <h2 className='lead-style flex items-center gap-2 font-medium'>
                                        <span aria-hidden className={cn('size-1.5 rounded-full', DOT_CLASS[stage.tone])} />
                                        {stage.value}
                                    </h2>
                                    <CountBadge>{stageDeals.length}</CountBadge>
                                </div>
                                <span className='caption-style text-subtle flex items-center gap-1 tabular-nums'>
                                    <span>${formatMoney(total)}</span>
                                    <span aria-hidden>·</span>
                                    <span>{stage.probability}% likely</span>
                                </span>
                            </header>

                            <div className='flex min-h-[120px] flex-1 flex-col gap-2 overflow-y-auto p-2 pt-1'>
                                {stageDeals.map(deal => {
                                    const company = deal.companyId ? companyIndex.get(deal.companyId) : undefined

                                    return (
                                        <DealCard
                                            key={deal.id}
                                            deal={deal}
                                            company={company}
                                            today={today}
                                            dragging={draggingId === deal.id}
                                            onEdit={() => openDialog('deal', { dealId: deal.id })}
                                            onOpenCompany={() => company && openDetail(company.id)}
                                            onMove={next => moveDeal(deal, next)}
                                            onDragStart={event => {
                                                event.dataTransfer.setData('text/plain', deal.id)
                                                event.dataTransfer.effectAllowed = 'move'
                                                setDraggingId(deal.id)
                                            }}
                                            onDragEnd={() => {
                                                setDraggingId(null)
                                                setDropStage(null)
                                            }}
                                        />
                                    )
                                })}
                                {stageDeals.length === 0 && (
                                    <div className='caption-style text-subtle border-line-strong/60 flex flex-1 items-center justify-center rounded-lg border border-dashed px-3 py-6 text-center'>
                                        {status === 'loading' ? "Loading deals…" : draggingId ? "Drop here" : "No deals in this stage"}
                                    </div>
                                )}
                            </div>
                        </section>
                    )
                })}
            </div>
        </>
    )
}
